import * as React from 'react';
import styled from 'styled-components';

import { Inline } from '../../layout/Inline';
import { Button } from '../../primitives/Button';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

/**
 * Legacy `Btn` `rounded-secondary` in `customRecurrenceModal.tsx` — not DS default `size="md"`.
 * @see src/components/button/button.styled.ts (`padding: 7×14`, `font-size: 12`)
 */
const DayButton = styled(Button)`
  && {
    min-height: unset;
    padding: ${rem(7)} ${rem(14)};
    font-size: ${rem(12)};
    line-height: ${rem(16)};
    font-weight: 400;
  }
`;

/** Cron / scheduler day keys (SUN–SAT) with single-letter labels (legacy custom recurrence parity). */
export const WEEKDAY_TOGGLE_DEFAULT_OPTIONS = [
  { value: 'SUN', label: 'S' },
  { value: 'MON', label: 'M' },
  { value: 'TUE', label: 'T' },
  { value: 'WED', label: 'W' },
  { value: 'THU', label: 'T' },
  { value: 'FRI', label: 'F' },
  { value: 'SAT', label: 'S' },
] as const;

export type WeekDayToggleOption = { value: string; label: string };

export interface WeekDayToggleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Selected day keys (multi-select). */
  value: string[];
  onChange: (next: string[]) => void;
  options?: readonly WeekDayToggleOption[];
  disabled?: boolean;
  semanticId?: string;
  /** Exposed on the group for `aria-labelledby` from FormField. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

function toggleDay(selected: string[], dayKey: string): string[] {
  return selected.includes(dayKey)
    ? selected.filter((d) => d !== dayKey)
    : [...selected, dayKey];
}

export const WeekDayToggle = React.forwardRef<HTMLDivElement, WeekDayToggleProps>(
  function WeekDayToggle(
    {
      value,
      onChange,
      options = WEEKDAY_TOGGLE_DEFAULT_OPTIONS,
      disabled = false,
      semanticId,
      'aria-label': ariaLabel = 'Days of week',
      'aria-labelledby': ariaLabelledBy,
      ...rest
    },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <Inline
        ref={ref}
        gap={rem(8)}
        wrap
        align="center"
        role="group"
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy}
        {...semanticProps}
        {...rest}
      >
        {options.map((day) => {
          const isSelected = value.includes(day.value);
          return (
            <DayButton
              key={day.value}
              type="button"
              variant="rounded-secondary"
              size="sm"
              label={day.label}
              selected={isSelected}
              disabled={disabled}
              semanticId={semanticId ? `${semanticId}-${day.value}` : undefined}
              aria-pressed={isSelected}
              onClick={() => {
                if (disabled) return;
                onChange(toggleDay(value, day.value));
              }}
            />
          );
        })}
      </Inline>
    );
  },
);

WeekDayToggle.displayName = 'WeekDayToggle';
